import { HOST, BACKEND_PORT } from './Networker';

// Path for internal api socket
const SOCKET_PATH = '/socket/';

export default class InternalApi
{
    // Return a Promise resolving to a WebSocket object to be used with further calls.
    static connect(onMessage)
    {
        // Whoever designed this (the dashboard) doesn't know what a host is....
        const hostUrl = new URL(HOST);
        const host = hostUrl.host;

        const protocol = (host === 'localhost' ? 'ws' : 'wss');
        const url = new URL(SOCKET_PATH, `${protocol}://${host}:${BACKEND_PORT}`);

        return new Promise((res, rej) => {
            const socket = new WebSocket(url);

            socket.addEventListener('error', (event) => {
                console.error(event);
                rej(event);
            }, { once: true });

            socket.addEventListener('open', (_) => res(socket), { once: true });

            if (onMessage) {
                socket.addEventListener('message', (event) => onMessage(JSON.parse(event.data), socket));
            }

            if (socket.readyState === 1) {
                res(socket);
            }
        });
    }

    static request(socket, endpoint, args, clientCallback)
    {
        if (socket.readyState > 1) {
            return Promise.reject('Already closing!');
        }

        return new Promise((res, rej) => {
            const onMessage = (event) => {
                const msg = JSON.parse(event.data);

                if (clientCallback) { clientCallback(msg, socket); }
                if (msg.function !== endpoint) { return; }

                if (msg.done)
                {
                    socket.removeEventListener('message', onMessage);
                    res(msg);
                }
            };

            socket.addEventListener('message', onMessage);

            socket.addEventListener('error', (event) => {
                socket.removeEventListener('message', onMessage);
                console.error(`${endpoint}:`, event);

                rej(event);
            }, { once: true });

            socket.send(JSON.stringify({
                function: endpoint,
                args: args
            }));
        });
    }

    static disconnect(socket)
    {
        if (socket.readyState === 3) {
            return Promise.resolve();
        }

        return new Promise((res, rej) => {
            socket.addEventListener('error', (event) => {
                console.error(event);
                rej(event);
            }, { once: true });

            socket.addEventListener('close', (_) => res(), { once: true });
            socket.close();
        });
    }

    // Instance-based version of the above.
    // This can also deal with transparent reconnect.
    constructor(onMessage) {
        this.onMessage = onMessage;
        this.connected = false;
        this.socket = null;

        // If this is non-null, we save a promise for reconnection
        //   so that all requests can wait on reconnection before sending.
        // This way we only ever attempt to reconnect once regardless of
        //   how many requests are pending.
        this.reconnect = null;
    }

    connect() {
        if (this.socket) { return Promise.resolve(); }

        return InternalApi.connect(this.onMessage).then((socket) => {
            console.info('Opened socket:', socket);
            this.connected = true;
            this.socket = socket;
        });
    }

    request(endpoint, args, clientCallback) {
        if (this.reconnect) {
            console.info(`New pending request after reconnection (endpoint=${endpoint})`);

            return this.reconnect.then((socket) => InternalApi.request(socket, endpoint, args, clientCallback));
        } else if (this.socket.readyState > 1) {
            // This socket is closing we have to make a new one and retry.
            return this._reconnect().then((socket) => InternalApi.request(socket, endpoint, args, clientCallback));
        } else {
            return InternalApi.request(this.socket, endpoint, args, clientCallback);
        }
    }

    _reconnect() {
        console.info('Reconnect socket.');

        if (!this.reconnect)
        {
            if (this.socket.readyState <= 1) {
                this.reconnect = Promise.resolve(this.socket);
            } else {
                this.reconnect = InternalApi.connect(this.onMessage).then((socket) => {
                    this.socket = socket;
                    return socket;
                });
            }
        }

        return this.reconnect;
    }

    disconnect() {
        if (!this.socket) { return Promise.resolve(); }

        return InternalApi.disconnect(this.socket).then(() => {
            this.connected = false;
            this.socket = null;
        });
    }
}
